import { Link } from 'react-router-dom'
import { Eye, Boxes, Download, Pencil } from 'lucide-react'
import { useState } from 'react'
import { Spinner } from '@/component/ui/spinner'
import { PageContainer } from '@/component/layout'

import { useCluster } from '@/hooks/use-cluster'
import { Toggle } from '@/component/ui/toogle'
import { toBool } from '@/utils/booleans'
import { ClusterQuickViewModal } from './cluster-quick-view'
import { AssociatedModalitiesQuickViewModal } from './cluster-associated-quick-view'

export const Cluster = () => {
  const { data, loading } = useCluster()
  const [quickViewId, setQuickViewId] = useState<string | null>(null)
  const [assocQuickViewId, setAssocQuickViewId] = useState<string | null>(null)

  const rows = Array.isArray(data) ? data : []
  const totalCount = data?.totalCount || rows.length

  const openQuickView = (id: string | number) => {
    setQuickViewId(id.toString())
  }

  const closeQuickView = () => {
    setQuickViewId(null)
  }

  const openAssocQuickView = (id: string | number) => {
    setAssocQuickViewId(id.toString())
  }

  const closeAssocQuickView = () => {
    setAssocQuickViewId(null)
  }

  // Sort Table and logic
  // const [sortField, setSortField] = useState<string | null>(null)
  // const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [sortField] = useState<string | null>(null)
  const [sortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: string) => {
    console.log('Sorting for ' + field + ' is disabled.');
    // if (sortField === field) {
    //   setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    // } else {
    //   setSortField(field)
    //   setSortDirection('asc')
    // }
  }

  const sortedRows = [...rows].sort((a, b) => {
    if (!sortField) return 0
    const aVal = a[sortField] ?? ''
    const bVal = b[sortField] ?? ''
    return sortDirection === 'asc' ? String(aVal).localeCompare(String(bVal)) : String(bVal).localeCompare(String(aVal))
  })

  // Updated loading state with spinner
  if (loading) {
    return (
      <PageContainer>
        <div className='flex flex-col items-center justify-center min-h-[400px] text-center'>
          <Spinner />
          <p className='mt-4 text-lg text-gray-600'>Loading clusters...</p>
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      {/* Cluster Manager Header */}
      <div className='mb-6'>
        <div className='flex items-center gap-2 mb-4'>
          <div className='w-8 h-8  bg-gray-200 rounded-full flex items-center justify-center'>
            <Boxes className='w-4 h-4 text-gray-500' />
          </div>
          <h1 className='text-3xl font-bold'>Cluster manager</h1>
        </div>

        <p className='text-sm text-gray-700 mb-6'>
          A modality cluster is a group of related learning activities, organised by shared principles, that together support a distinctive way of learning and
          teaching. Below is a list of the identified modality clusters.
        </p>

        <div className='flex items-center gap-2 mb-4'>
          <h3 className='text-base font-semibold'>Cluster list</h3>
          {/* <Info className='w-4 h-4 text-gray-500' /> */}
        </div>

        <p className='text-sm text-gray-600 mb-6'>
          Showing {rows.length} out of {totalCount} results
        </p>
      </div>

      {/* Table */}
      <div className='overflow-x-auto'>
        <table className='min-w-full text-left'>
          <thead className='bg-gray-50 text-sm font-semibold text-gray-700'>
            <tr>
              {/* Enable Checkbox when flow is ready */}
              {/* <th className='px-4 py-3'>
                <input type='checkbox' className='h-4 w-4 rounded border-gray-300' />
              </th> */}
              {/* Cluster Name */}
              <th className='px-4 py-3 cursor-pointer' onClick={() => handleSort('name')}>
                Cluster Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              {/* Associated Modalities (TBC) */}
              {/* <th className='px-4 py-3 cursor-pointer' onClick={() => handleSort('name')}> */}
              {/* Associated Modalities (TBC) {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')} */}
              {/* </th> */}
              <th className='px-4 py-3'>Associated Modalities</th>

              {/* Created by */}
              <th className='px-4 py-3 cursor-pointer' onClick={() => handleSort('createdBy')}>
                Created by {sortField === 'createdBy' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              {/* Date Added */}
              <th className='px-4 py-3 cursor-pointer' onClick={() => handleSort('created')}>
                Date Added {sortField === 'created' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>

              <th className='px-4 py-3 text-center'>Status</th>
              <th className='px-4 py-3 text-center'>Actions</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {sortedRows.length ? (
              sortedRows.map((row: any) => {
                const checked = toBool(row.isActive ?? row.status ?? row.is_active)
                return (
                  <tr key={row.id} className='hover:bg-gray-50'>
                    {/* Enable Checkbox when flow is ready */}
                    {/* <td className='px-4 py-3'>
                      <input type='checkbox' className='h-4 w-4 rounded border-gray-300' />
                    </td> */}
                    <td className='px-4 py-3 font-medium'>
                      <Link to={`/clusters/${row.id}`} className='text-blue-700 outline-none hover:underline focus:underline'>
                        {row.name}
                      </Link>
                    </td>
                    <td className='px-4 py-3 text-gray-700'>
                      <button
                        type='button'
                        onClick={() => openAssocQuickView(row.id)}
                        className='text-blue-700 hover:underline outline-none'
                        title={`View modalities associated with ${row.name}`}>
                        View Associated Modalities
                      </button>
                    </td>
                    <td className='px-4 py-3'>{row.createdBy ?? '—'}</td>
                    <td className='px-4 py-3'>
                      {row.created
                        ? new Date(row.created).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })
                        : '—'}
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <Toggle
                        checked={checked}
                        onChange={() => {}}
                        aria-checked={checked}
                        label={`Toggle status for ${row.name}`}
                        tone='deepBlue'
                        size='md'
                        disabled
                      />
                    </td>
                    <td className='px-4 py-3 text-center'>
                      <div className='mx-auto flex w-max items-center justify-center gap-3 text-gray-500'>
                        <button onClick={() => openQuickView(row.id)} title='Quick View' className='inline-flex'>
                          <Eye className='h-5 w-5 hover:text-gray-800' />
                        </button>
                        <button title='Download' disabled>
                          <Download className='h-5 w-5 hover:text-gray-800 cursor-not-allowed opacity-50 ' />
                        </button>
                        <button title='Edit' disabled>
                          <Pencil className='h-5 w-5 hover:text-gray-800 cursor-not-allowed opacity-50' />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan={7} className='px-4 py-6 text-center text-gray-500'>
                  No clusters found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Quick View Modal */}
      {quickViewId && <ClusterQuickViewModal clusterId={quickViewId} isOpen={!!quickViewId} onClose={closeQuickView} />}
      {assocQuickViewId && <AssociatedModalitiesQuickViewModal clusterId={assocQuickViewId} isOpen={!!assocQuickViewId} onClose={closeAssocQuickView} />}
    </PageContainer>
  )
}
